import { Currency } from '../locale/currencies';
import { UserType } from '../user';
import { CreateCustomerRequest } from './create-customer';
import { CreateSubscriptionRequest } from './create-subscription';
import { CreateUserRequest } from './create-user';

export * from './create-customer';
export * from './create-subscription';
export * from './create-user';

export type Type = 'zero' | 'pay-as-you-go' | 'individual' | 'business' | 'shopify' | 'flights';

export const inferIntentTypeFromClientSecret = (clientSecret: ClientSecret | null): IntentType => {
  if (clientSecret) {
    if (clientSecret.startsWith('seti')) return IntentType.Setup;
    if (clientSecret.startsWith('pi')) return IntentType.Payment;
  }

  return IntentType.None;
};

export enum IntentType {
  // Immediate payments
  Payment,
  // Setup/confirm cards for future payments
  Setup,
  // No card details or not being saved
  None,
}

// Whatever version of TS we're using doesn't like template literal types :(
type ClientSecret = string; // `seti_${string}` | `pi_${string}`;

type SubscriptionCreatedResponse = {
  clientSecret: ClientSecret | null;
  subscriptionId: number;
  currency: Currency;
  paymentAmount: number;
};

export type ResponsePathwaySpecificProperties = {
  zero: {};
  'pay-as-you-go': {};
  business: SubscriptionCreatedResponse;
  individual: SubscriptionCreatedResponse;
  shopify: {
    passwordResetUrl: string;
  };
  flights: {};
};

export type BaseRequest = {
  user: CreateUserRequest;
  customer: CreateCustomerRequest;
  testClockId?: string;
};

type ZeroRequest = BaseRequest & { type: 'zero' };

export type PayAsYouGoRequest = BaseRequest & { type: 'pay-as-you-go'; captchaToken: string };

export type BusinessRequest = BaseRequest & {
  type: 'business';
  subscription: CreateSubscriptionRequest;
  captchaToken: string;
};

type IndividualRequest = BaseRequest & {
  type: 'individual';
  subscription: CreateSubscriptionRequest;
  captchaToken: string;
};

export type ShopifyRequest = Omit<BaseRequest, 'user'> & {
  type: 'shopify';
  user: Omit<CreateUserRequest, 'password'>;
  shopDomain?: string;
};

export type FlightsRequest = Omit<BaseRequest, 'user'> & {
  type: 'flights';
  captchaToken: string;
  user: Omit<CreateUserRequest, 'password'>;
};

export type Request =
  | ZeroRequest
  | PayAsYouGoRequest
  | BusinessRequest
  | IndividualRequest
  | ShopifyRequest
  | FlightsRequest;

export type Response<T extends Type = Type> = ResponsePathwaySpecificProperties[T] & {
  user: UserType;
  authentication: { idToken: string; expiresIn: number; accessToken: string; refreshToken?: string } | null;
};
